import { queuesForVariation } from '@axle-health/work-queue-definitions';
import { Ordering } from 'gql-types';

import { ContainerBulkAction } from '../ContainerVisitTable.types';
import {
    defaultEpisodeOfCareRowToLink,
    defaultEpisodeOrdersRowToLink,
    defaultEpisodeVisitsRowToLink,
    defaultVisitsRowToLink,
} from '../definitions';
import { definitionFor, fromDefinition } from '../fromDefinition';
import {
    EpisodeOfCareDashboardContainer,
    OrderDashboardContainer,
    PatientDashboardContainer,
    VisitDashboardContainer,
} from '../types';
import { HH_CONTAINER_KEYS } from './containerKeys/containerKeys';
import { IDashboardVariationConfig } from './types';

/**
 * Home Health demo dashboard config, following Pinnacle Health's.
 */

const PUBLISHED_QUEUES = queuesForVariation('HOME_HEALTH_DEMO');

const patientSchedulingLink = ({ id }: { id: string }): string => `/patients/${id}/scheduling`;
const allEpisodesSchedulingLink = ({ id }: { id: string }): string =>
    `/patients/${id}/scheduling?episode=all`;

const CANCEL_VISITS = { bulkActions: [ContainerBulkAction.CANCEL_VISITS] };

const INCOMPLETE_CARE_TEAM_CONTAINER: PatientDashboardContainer = fromDefinition(
    definitionFor(PUBLISHED_QUEUES, HH_CONTAINER_KEYS.incompleteCareTeam),
    {
        defaultEnabled: false,
        hoverText: 'Patients that need care teams assigned.',
        tableConfig: {
            rowToLink: patientSchedulingLink,
            emptyTableText: 'No patients with incomplete care teams.',
            ordering: { fullName: Ordering.ASC },
        },
    },
);

const SOC_CONTAINER: VisitDashboardContainer = fromDefinition(
    definitionFor(PUBLISHED_QUEUES, HH_CONTAINER_KEYS.soc),
    {
        defaultEnabled: true,
        hoverText: 'SOCs that need to be scheduled.',
        tableConfig: {
            highlightLinkedVisit: true,
            rowToLink: defaultVisitsRowToLink,
            emptyTableText: 'No SOC visits to schedule.',
            ordering: { effectiveSortDate: Ordering.ASC },
        },
    },
);

const ROC_CONTAINER: VisitDashboardContainer = fromDefinition(
    definitionFor(PUBLISHED_QUEUES, HH_CONTAINER_KEYS.roc),
    {
        defaultEnabled: true,
        hoverText: 'ROCs that need to be scheduled.',
        tableConfig: {
            highlightLinkedVisit: true,
            rowToLink: defaultVisitsRowToLink,
            emptyTableText: 'No ROC visits to schedule.',
            ordering: { effectiveSortDate: Ordering.ASC },
        },
    },
);

const EVAL_CONTAINER: VisitDashboardContainer = fromDefinition(
    definitionFor(PUBLISHED_QUEUES, HH_CONTAINER_KEYS.eval),
    {
        defaultEnabled: true,
        hoverText: 'Evals that need to be scheduled.',
        tableConfig: {
            highlightLinkedVisit: true,
            rowToLink: defaultVisitsRowToLink,
            emptyTableText: 'No Eval visits to schedule.',
            ordering: { effectiveSortDate: Ordering.ASC },
        },
    },
);

const URGENT_VISITS_NEEDING_SCHEDULING_CONTAINER: VisitDashboardContainer = fromDefinition(
    definitionFor(PUBLISHED_QUEUES, HH_CONTAINER_KEYS.expiringFulfillmentWindow),
    {
        defaultEnabled: true,
        hoverText: 'Visits that need to be scheduled within the next 7 days.',
        tableConfig: {
            highlightLinkedVisit: true,
            rowToLink: defaultVisitsRowToLink,
            emptyTableText: 'No visits with expiring fulfillment windows.',
            ordering: { effectiveSortDate: Ordering.ASC },
        },
    },
);

const PATIENTS_NEEDING_SCHEDULING_CONTAINER: EpisodeOfCareDashboardContainer = fromDefinition(
    definitionFor(PUBLISHED_QUEUES, HH_CONTAINER_KEYS.patientsNeedingScheduling),
    {
        defaultEnabled: true,
        hoverText: 'Patients with visits that need to be scheduled.',
        tableConfig: {
            rowToLink: defaultEpisodeOfCareRowToLink,
            emptyTableText: 'No patients to schedule.',
            ordering: { patient: { fullName: Ordering.ASC } },
        },
    },
);

const OVERSCHEDULED_PATIENTS_CONTAINER: OrderDashboardContainer = fromDefinition(
    definitionFor(PUBLISHED_QUEUES, HH_CONTAINER_KEYS.overscheduledPatients),
    {
        defaultEnabled: true,
        hoverText: 'Patients who have an overscheduled frequency.',
        tableConfig: {
            rowToLink: defaultEpisodeOrdersRowToLink,
            emptyTableText: 'No overscheduled patients.',
            ordering: { fulfillmentWindowStartDate: Ordering.ASC },
        },
    },
);

const UNDERSCHEDULED_PATIENTS_CONTAINER: OrderDashboardContainer = fromDefinition(
    definitionFor(PUBLISHED_QUEUES, HH_CONTAINER_KEYS.underscheduledPatients),
    {
        defaultEnabled: true,
        hoverText: 'Frequency orders with fewer active visits than the order requires.',
        tableConfig: {
            rowToLink: defaultEpisodeOrdersRowToLink,
            emptyTableText: 'No frequency orders needing review.',
            ordering: { fulfillmentWindowStartDate: Ordering.ASC },
        },
    },
);

const INACTIVE_CLINICIAN_VISITS_CONTAINER: VisitDashboardContainer = fromDefinition(
    definitionFor(PUBLISHED_QUEUES, HH_CONTAINER_KEYS.inactiveClinicianVisits),
    {
        defaultEnabled: true,
        hoverText: 'Visits assigned to an inactive clinician.',
        tableConfig: {
            highlightLinkedVisit: true,
            rowToLink: defaultEpisodeVisitsRowToLink,
            emptyTableText: 'No visits assigned to inactive clinicians.',
            ordering: { effectiveSortDate: Ordering.ASC },
        },
    },
);

const AGENCY_DISCHARGED_PATIENTS_WITH_PENDING_VISITS_CONTAINER: VisitDashboardContainer =
    fromDefinition(definitionFor(PUBLISHED_QUEUES, HH_CONTAINER_KEYS.dischargeWithPendingVisits), {
        defaultEnabled: false,
        hoverText: 'Agency discharged patients with scheduled visits that need to be cancelled.',
        extraProps: CANCEL_VISITS,
        tableConfig: {
            highlightLinkedVisit: true,
            rowToLink: defaultEpisodeVisitsRowToLink,
            emptyTableText: 'No agency discharged patients with pending visits.',
            ordering: { effectiveSortDate: Ordering.ASC },
        },
    });

const DISCIPLINE_DISCHARGE_PATIENTS_WITH_PENDING_VISITS_CONTAINER: VisitDashboardContainer =
    fromDefinition(
        definitionFor(
            PUBLISHED_QUEUES,
            HH_CONTAINER_KEYS.disciplineDischargePatientsWithPendingVisits,
        ),
        {
            defaultEnabled: false,
            hoverText:
                'Discipline discharged patients with scheduled visits that need to be canceled.',
            extraProps: CANCEL_VISITS,
            tableConfig: {
                highlightLinkedVisit: true,
                rowToLink: defaultEpisodeVisitsRowToLink,
                emptyTableText: 'No pending visits after discipline discharge.',
                ordering: { effectiveSortDate: Ordering.ASC },
            },
        },
    );

const DISCONTINUED_FREQUENCIES_WITH_PENDING_VISITS_CONTAINER: VisitDashboardContainer =
    fromDefinition(
        definitionFor(PUBLISHED_QUEUES, HH_CONTAINER_KEYS.discontinuedFrequenciesWithPendingVisits),
        {
            defaultEnabled: false,
            hoverText: 'Discontinued frequencies with scheduled visits that need to be cancelled.',
            extraProps: CANCEL_VISITS,
            tableConfig: {
                highlightLinkedVisit: true,
                rowToLink: defaultEpisodeVisitsRowToLink,
                emptyTableText: 'No visits tied to discontinued frequencies.',
                ordering: { effectiveSortDate: Ordering.ASC },
            },
        },
    );

const TRANSFERRED_PATIENTS_WITH_PENDING_VISITS_CONTAINER: VisitDashboardContainer = fromDefinition(
    definitionFor(PUBLISHED_QUEUES, HH_CONTAINER_KEYS.transfers),
    {
        defaultEnabled: false,
        hoverText: 'Transferred patients with scheduled visits that need to be cancelled.',
        extraProps: CANCEL_VISITS,
        tableConfig: {
            highlightLinkedVisit: true,
            rowToLink: defaultEpisodeVisitsRowToLink,
            emptyTableText: 'No transfers requiring attention.',
            ordering: { effectiveSortDate: Ordering.ASC },
        },
    },
);

const PENDING_HHA_SUPERVISORY_CONTAINER: PatientDashboardContainer = fromDefinition(
    definitionFor(PUBLISHED_QUEUES, HH_CONTAINER_KEYS.pendingHhaSupervisory),
    {
        defaultEnabled: false,
        hoverText:
            'Patients needing an HHA Supervisory visit within 14 days of their last supervisory event.',
        tableConfig: {
            rowToLink: allEpisodesSchedulingLink,
            emptyTableText: 'No patients pending HHA supervisory visits.',
            ordering: { fullName: Ordering.ASC },
        },
    },
);

const PENDING_LPN_SUPERVISORY_CONTAINER: PatientDashboardContainer = fromDefinition(
    definitionFor(PUBLISHED_QUEUES, HH_CONTAINER_KEYS.pendingLpnSupervisory),
    {
        defaultEnabled: false,
        hoverText:
            'Patients needing an LPN Supervisory visit within 30 days of their last supervisory event.',
        tableConfig: {
            rowToLink: allEpisodesSchedulingLink,
            emptyTableText: 'No patients pending LPN supervisory visits.',
            ordering: { fullName: Ordering.ASC },
        },
    },
);

const PENDING_THERAPY_SUPERVISORY_CONTAINER: PatientDashboardContainer = fromDefinition(
    definitionFor(PUBLISHED_QUEUES, HH_CONTAINER_KEYS.pendingTherapySupervisory),
    {
        defaultEnabled: false,
        hoverText: 'Patients needing a PT or OT Supervisory visit within the next 7 days.',
        tableConfig: {
            rowToLink: allEpisodesSchedulingLink,
            emptyTableText: 'No patients pending therapy supervisory visits.',
            ordering: { fullName: Ordering.ASC },
        },
    },
);

const PENDING_THERAPY_REEVALUATION_CONTAINER: PatientDashboardContainer = fromDefinition(
    definitionFor(PUBLISHED_QUEUES, HH_CONTAINER_KEYS.pendingReassessment),
    {
        defaultEnabled: false,
        hoverText:
            'Patients needing a PT, OT, or ST re-eval within 30 days of their last reassessment.',
        tableConfig: {
            rowToLink: patientSchedulingLink,
            emptyTableText: 'No patients pending re-evaluation.',
            ordering: { fullName: Ordering.ASC },
        },
    },
);

const UPCOMING_END_OF_EPISODE_CONTAINER: EpisodeOfCareDashboardContainer = fromDefinition(
    definitionFor(PUBLISHED_QUEUES, HH_CONTAINER_KEYS.pendingEndOfEpisode),
    {
        defaultEnabled: false,
        hoverText:
            'Patients nearing the end of their episode with no recert, discharge, or recent ROC visit scheduled.',
        tableConfig: {
            rowToLink: defaultEpisodeOfCareRowToLink,
            emptyTableText: 'No episodes pending end of care.',
            ordering: { patient: { fullName: Ordering.ASC } },
        },
    },
);

const VISITS_SCHEDULED_BEFORE_SOC_CONTAINER: VisitDashboardContainer = fromDefinition(
    definitionFor(PUBLISHED_QUEUES, HH_CONTAINER_KEYS.evalsBeforeSocs),
    {
        defaultEnabled: false,
        hoverText: "Visits that are scheduled or completed prior to the patient's SOC.",
        tableConfig: {
            highlightLinkedVisit: true,
            rowToLink: defaultVisitsRowToLink,
            emptyTableText: 'No Evals scheduled before SOCs.',
            ordering: { effectiveSortDate: Ordering.ASC },
        },
    },
);

const CONFLICTING_DISCHARGE_AND_RECERT_VISITS_CONTAINER: EpisodeOfCareDashboardContainer =
    fromDefinition(definitionFor(PUBLISHED_QUEUES, HH_CONTAINER_KEYS.multipleOasisPerEpisode), {
        defaultEnabled: false,
        hoverText: 'Patients with more than 1 discharge or recert visit on an episode.',
        tableConfig: {
            rowToLink: defaultEpisodeOfCareRowToLink,
            emptyTableText: 'No episodes with multiple discharge or recert visits.',
            ordering: { patient: { fullName: Ordering.ASC } },
        },
    });

const UPCOMING_DISCHARGE_WITH_FUTURE_PENDING_VISITS_CONTAINER: VisitDashboardContainer =
    fromDefinition(
        definitionFor(PUBLISHED_QUEUES, HH_CONTAINER_KEYS.upcomingDischargeWithFuturePendingVisits),
        {
            defaultEnabled: false,
            hoverText:
                'Patients with an upcoming discharge who still have other visits scheduled after the discharge date.',
            tableConfig: {
                highlightLinkedVisit: true,
                rowToLink: defaultEpisodeVisitsRowToLink,
                emptyTableText: 'No patients with visits after discharge.',
                ordering: { effectiveSortDate: Ordering.ASC },
            },
        },
    );

const COMPLETED_VISITS_MISSING_AN_ORDER_CONTAINER: VisitDashboardContainer = fromDefinition(
    definitionFor(PUBLISHED_QUEUES, HH_CONTAINER_KEYS.completedVisitsNotMappedToOrders),
    {
        defaultEnabled: false,
        hoverText: "Completed SOCs/ROCs/Evals that aren't yet associated to an order.",
        tableConfig: {
            highlightLinkedVisit: true,
            rowToLink: defaultVisitsRowToLink,
            emptyTableText: 'No completed SOC/ROC/Eval visits unlinked to orders.',
            ordering: { effectiveSortDate: Ordering.ASC },
        },
    },
);

const ACTIVE_PATIENTS_WITH_NO_VISITS_CONTAINER: PatientDashboardContainer = fromDefinition(
    definitionFor(PUBLISHED_QUEUES, HH_CONTAINER_KEYS.noFutureAppointments),
    {
        defaultEnabled: false,
        hoverText: "Patients who are active but don't have any future visits scheduled.",
        tableConfig: {
            rowToLink: patientSchedulingLink,
            emptyTableText: 'No active patients without pending visits.',
            ordering: { fullName: Ordering.ASC },
        },
    },
);

const ALL_TRANSFERRED_PATIENTS_CONTAINER: EpisodeOfCareDashboardContainer = fromDefinition(
    definitionFor(PUBLISHED_QUEUES, HH_CONTAINER_KEYS.allTransferredPatients),
    {
        defaultEnabled: false,
        hoverText: 'All patients currently in transfer status.',
        tableConfig: {
            rowToLink: defaultEpisodeOfCareRowToLink,
            emptyTableText: 'No episodes pending in transfer.',
            ordering: { patient: { fullName: Ordering.ASC } },
        },
    },
);

const MISSED_VISITS_CONTAINER: VisitDashboardContainer = fromDefinition(
    definitionFor(PUBLISHED_QUEUES, HH_CONTAINER_KEYS.missedVisits),
    {
        defaultEnabled: false,
        hoverText: 'All missed visits scheduled in the current Medicare week.',
        tableConfig: {
            rowToLink: defaultVisitsRowToLink,
            emptyTableText: 'No missed visits this Medicare week.',
            ordering: { effectiveSortDate: Ordering.ASC },
        },
    },
);

const VISITS_WITHOUT_SHIFTS_CONTAINER: VisitDashboardContainer = fromDefinition(
    definitionFor(PUBLISHED_QUEUES, HH_CONTAINER_KEYS.visitsWithoutActiveShifts),
    {
        defaultEnabled: true,
        hoverText: 'Visits without active shifts',
        extraProps: { showClinicianColumn: true },
        tableConfig: {
            highlightLinkedVisit: true,
            rowToLink: defaultVisitsRowToLink,
            emptyTableText: 'No visits without shifts.',
            ordering: { effectiveSortDate: Ordering.ASC },
        },
    },
);

export const HOME_HEALTH_DASHBOARD_CONFIG: IDashboardVariationConfig = {
    containers: [
        INCOMPLETE_CARE_TEAM_CONTAINER,
        SOC_CONTAINER,
        ROC_CONTAINER,
        EVAL_CONTAINER,
        URGENT_VISITS_NEEDING_SCHEDULING_CONTAINER,
        PATIENTS_NEEDING_SCHEDULING_CONTAINER,
        OVERSCHEDULED_PATIENTS_CONTAINER,
        UNDERSCHEDULED_PATIENTS_CONTAINER,
        INACTIVE_CLINICIAN_VISITS_CONTAINER,
        AGENCY_DISCHARGED_PATIENTS_WITH_PENDING_VISITS_CONTAINER,
        DISCIPLINE_DISCHARGE_PATIENTS_WITH_PENDING_VISITS_CONTAINER,
        DISCONTINUED_FREQUENCIES_WITH_PENDING_VISITS_CONTAINER,
        TRANSFERRED_PATIENTS_WITH_PENDING_VISITS_CONTAINER,
        PENDING_HHA_SUPERVISORY_CONTAINER,
        PENDING_LPN_SUPERVISORY_CONTAINER,
        PENDING_THERAPY_SUPERVISORY_CONTAINER,
        PENDING_THERAPY_REEVALUATION_CONTAINER,
        UPCOMING_END_OF_EPISODE_CONTAINER,
        VISITS_SCHEDULED_BEFORE_SOC_CONTAINER,
        CONFLICTING_DISCHARGE_AND_RECERT_VISITS_CONTAINER,
        UPCOMING_DISCHARGE_WITH_FUTURE_PENDING_VISITS_CONTAINER,
        COMPLETED_VISITS_MISSING_AN_ORDER_CONTAINER,
        ACTIVE_PATIENTS_WITH_NO_VISITS_CONTAINER,
        ALL_TRANSFERRED_PATIENTS_CONTAINER,
        MISSED_VISITS_CONTAINER,
        VISITS_WITHOUT_SHIFTS_CONTAINER,
    ],
};
